<template>
  <section class="feedback-admin-container tw-page">
    <PageHeader title="Rejected Manager Verifications" />

    <b-table
      v-if="data && data.length"
      :data="data"
      :columns="tableColumns"
      :paginated="true"
      :per-page="15"
      :striped="true"
      :hoverable="true"
      :default-sort="['responseDate', 'desc']"
      :sortable="true"
      class="feedback-table"
      @click="onRowClick"
    >
      <template #empty>
        <div class="empty-msg">No rejected manager verifications found.</div>
      </template>
    </b-table>

    <p v-if="error" class="error-msg">{{ error }}</p>

    <!-- USER JSON MODAL -->
    <b-modal
      v-model="showUserModal"
      has-modal-card
      trap-focus
    >
      <div class="modal-card" style="max-width: 900px;">
        <header class="modal-card-head">
          <h2 class="modal-card-title">User Raw Data</h2>
          <button class="delete" @click="showUserModal = false"></button>
        </header>

        <section class="modal-card-body">

          <!-- ACTIONS -->
          <div class="button-row">
            <b-button size="is-small" type="is-primary" @click="copyJson">
              Copy JSON
            </b-button>
            <span v-if="copySuccess" class="copy-success">Copied!</span>
          </div>

          <!-- RAW SYNTAX-HIGHLIGHTED JSON -->
          <pre class="json-highlight" v-html="highlightedJson"></pre>

        </section>
      </div>
    </b-modal>
  </section>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader.vue'
import moment from 'moment';
import { useUserStore } from '@/stores/userStore';
import { getUser } from '../../utils/helpers.js';


export default {
  name: 'RejectedManVerifications',
  components: {
    PageHeader,
  },

  data() {
    return {
      user: null,
      data: [],
      error: '',
      searchTerm: '',
      selectedUser: null,
      showUserModal: false,
      copySuccess: false,
      filteredTree: null,

      config: {
        headers: { Authorization: this.$cookies.get('teamwrkr-token') }
      },

      tableColumns: [
        { field: 'reviewerFirstName', label: 'Manager First Name', sortable: true },
        { field: 'reviewerLastName', label: 'Manager Last Name', sortable: true },
        { field: 'reviewerEmail', label: 'Manager Email', sortable: true },
        { field: 'requestDate', label: 'Verification Request Date', sortable: true },
        { field: 'responseDate', label: 'Verification Response Date', sortable: true }
      ]
    };
  },

  computed: {
    baseTree() {
      return this.selectedUser ? this.buildTree(this.selectedUser) : [];
    },

    treeData() {
      return this.filteredTree || this.baseTree;
    },

    formattedJson() {
      return this.selectedUser
        ? JSON.stringify(this.selectedUser, null, 2)
        : '';
    },

    highlightedJson() {
      return this.syntaxHighlight(this.formattedJson);
    }
  },

  created() {
    this.userStore = useUserStore();
    this.user = this.userStore.user;

    if (this.user && this.user._id) {
      this.getManagerVerifications();
    } else {
      this.fetchUserData();
    }
  },

  methods: {
    /* ------------------------------------------------------------------------
       DATA FETCHING
    ------------------------------------------------------------------------ */
    async getManagerVerifications() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/rejected-manager-verifications`,
          this.config
        );

        console.log(response.data)

        this.data = response.data.map(row => ({
          ...row,
          reviewerFirstName: row.reviewer?.firstName || '-',
          reviewerLastName: row.reviewer?.lastName || '-',
          reviewerEmail: row.reviewer?.email || row.reviewerEmail || '-',
          requestDate: row.requestDate
            ? moment(row.requestDate).format('MMMM Do YYYY, h:mm:ss a')
            : '-',
          responseDate: row.responseDate
            ? moment(row.responseDate).format('MMMM Do YYYY, h:mm:ss a')
            : '-'
        }));
      } catch (err) {
        this.error = err?.response?.data?.message || 'Could not get rejected manager verifications. Please try again.';
      }
    },

    async fetchUserData() {
      try {
        const token = this.$cookies.get('teamwrkr-token');
        const userData = await getUser(token);
        this.userStore.setUser(userData);
        this.user = userData;
        this.getManagerVerifications();
      } catch (err) {
        this.error = 'Error fetching user data';
      }
    },

    /* ------------------------------------------------------------------------
       TABLE ROW CLICK
    ------------------------------------------------------------------------ */
    onRowClick(payload) {
      const row = payload && payload.row ? payload.row : payload;
      if (!row) return;

      this.selectedUser = row;
      this.searchTerm = '';
      this.filteredTree = null;
      this.copySuccess = false;
      this.showUserModal = true;
    },

    /* ------------------------------------------------------------------------
       COPY JSON
    ------------------------------------------------------------------------ */
    copyJson() {
      if (!this.formattedJson) return;

      navigator.clipboard.writeText(this.formattedJson).then(() => {
        this.copySuccess = true;
        setTimeout(() => (this.copySuccess = false), 2000);
      });
    },

    /* ------------------------------------------------------------------------
       TREE BUILDER
    ------------------------------------------------------------------------ */
    buildTree(obj) {
      if (obj === null || typeof obj !== 'object') {
        return { value: obj, isLeaf: true };
      }

      return Object.entries(obj).map(([key, value]) => ({
        key,
        value: value !== null && typeof value === 'object'
          ? this.buildTree(value)
          : value,
        isLeaf: value === null || typeof value !== 'object',
        _expanded: false
      }));
    },

    toggleNode(node) {
      node._expanded = !node._expanded;
    },

    /* ------------------------------------------------------------------------
       JSON TREE SEARCH
    ------------------------------------------------------------------------ */
    applySearch() {
      const term = this.searchTerm.trim().toLowerCase();

      if (!term) {
        this.filteredTree = null;
        return;
      }

      const matchNode = node => {
        if (node.isLeaf) {
          const val = String(node.value).toLowerCase();
          return node.key.toLowerCase().includes(term) || val.includes(term)
            ? node
            : null;
        }

        const children = node.value
          .map(child => matchNode(child))
          .filter(Boolean);

        if (children.length) {
          return { ...node, _expanded: true, value: children };
        }

        return node.key.toLowerCase().includes(term) ? node : null;
      };

      this.filteredTree = this.baseTree
        .map(root => matchNode(root))
        .filter(Boolean);
    },

    /* ------------------------------------------------------------------------
       JSON SYNTAX HIGHLIGHT (lint-safe)
    ------------------------------------------------------------------------ */
    syntaxHighlight(json) {
      if (!json) return '';

      json = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][-+]?\d+)?)/g,
        match => {
          let cls = '';
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? 'key' : 'string';
          } else if (/true|false/.test(match)) {
            cls = 'boolean';
          } else if (/null/.test(match)) {
            cls = 'null';
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.feedback-admin-container {
  margin: $space-7 auto;
  padding: $space-6 $space-4;
  background: $color-surface-alt;
  border-radius: $radius-xl;
  box-shadow: 0 4px 32px rgba(170, 118, 214, 0.07);
  color: $color-text;
}

.feedback-table {
  background: $color-surface;
  border-radius: $radius-lg;
  box-shadow: 0 2px 12px rgba(170, 118, 214, 0.05);
  padding: $space-4;
}

.empty-msg {
  text-align: center;
  color: $color-accent;
  font-weight: 600;
  padding: $space-6 0;
}

.error-msg {
  color: $color-accent;
  font-weight: 600;
  margin-top: $space-4;
  text-align: center;
}

/* Modal UI */
.button-row {
  display: flex;
  gap: $space-3;
  margin-bottom: $space-4;
  align-items: center;
}

.copy-success {
  color: $color-success;
  font-weight: 600;
}

/* Tree viewer */
.tree-viewer {
  background: $color-surface-muted;
  border: 1px solid $color-border;
  border-radius: $radius-md;
  padding: $space-4;
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: $space-5;
}

.tree-line {
  display: flex;
  gap: 8px;
  cursor: pointer;
  font-family: monospace;
  padding: 4px 0;
}

.tree-children {
  margin-left: $space-5;
  border-left: 1px dashed $color-border;
  padding-left: $space-4;
}

/* Syntax-highlighted JSON */
.json-highlight {
  background: #1e1e1e;
  color: #dcdcdc;
  padding: $space-4;
  border-radius: $radius-md;
  white-space: pre;
  font-family: "Fira Code", monospace;
  overflow-x: auto;
  text-align: left;
}

.json-highlight .key {
  color: #9cdcfe;
}

.json-highlight .string {
  color: #ce9178;
}

.json-highlight .number {
  color: #b5cea8;
}

.json-highlight .boolean {
  color: #569cd6;
}

.json-highlight .null {
  color: #808080;
}
</style>
